import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const PREF_KEY = 'crash_reports_enabled';

/**
 * Wraps @capacitor-firebase/crashlytics behind a preference gate.
 * On web, all native calls are silently skipped.
 * Default state is enabled — users must explicitly opt out.
 */
@Injectable({ providedIn: 'root' })
export class CrashlyticsService {

  /** Returns the stored user preference. Defaults to true (opted in). */
  async isEnabled(): Promise<boolean> {
    const { value } = await Preferences.get({ key: PREF_KEY });
    return value === null ? true : value === 'true';
  }

  /** Persists the preference and immediately applies it to the native SDK. */
  async setEnabled(enabled: boolean): Promise<void> {
    await Preferences.set({ key: PREF_KEY, value: String(enabled) });
    await this.applyCollectionEnabled(enabled);
  }

  /**
   * Called once at app startup (platform.ready) to honour whatever the user
   * last chose.  On first launch this will enable collection by default.
   */
  async applyStoredPreference(): Promise<void> {
    const enabled = await this.isEnabled();
    await this.applyCollectionEnabled(enabled);
  }

  /** Log a breadcrumb message to Crashlytics. */
  async log(message: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    const { FirebaseCrashlytics } = await import('@capacitor-firebase/crashlytics');
    await FirebaseCrashlytics.log({ message });
  }

  /** Record a non-fatal exception / handled error. */
  async recordException(message: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    const { FirebaseCrashlytics } = await import('@capacitor-firebase/crashlytics');
    await FirebaseCrashlytics.recordException({ message });
  }

  private async applyCollectionEnabled(enabled: boolean): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    const { FirebaseCrashlytics } = await import('@capacitor-firebase/crashlytics');
    await FirebaseCrashlytics.setEnabled({ enabled });
  }
}
